import { useState } from 'react';
import { api, ago, humanise } from '../api';
import { PageHead, Panel, State, Table, kycTone, useDebounced, useLoad } from '../ui';
import { Person } from './Person';

/** Every driver, including anyone who stopped after choosing "I drive". */
export function Drivers() {
  const [q, setQ] = useState('');
  const search = useDebounced(q);
  const [open, setOpen] = useState<string | null>(null);
  const { data, error, loading, reload } = useLoad(() => api.peopleDrivers(search), { deps: [search] });

  if (open) return <Person id={open} mode="admin" onClose={() => setOpen(null)} />;

  return (
    <>
      <PageHead
        title="Drivers"
        blurb="Everyone who signed up to drive — stuck in verification first. Open a driver for their vehicle, every uploaded document, routes and trips."
      />
      <Panel
        title={`${data?.length ?? 0} drivers`}
        right={
          <>
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, phone, email, plate…" style={{ width: 220 }} />
            <button className="btn sm" onClick={() => void reload()} disabled={loading}>Refresh</button>
          </>
        }
      >
        {error ? <div className="empty">{error}</div> : (
          <Table
            rows={data}
            keyOf={(d) => d.id}
            onRow={(d) => setOpen(d.id)}
            empty={q ? 'Nobody matches that.' : 'No drivers registered yet.'}
            columns={[
              { head: 'Driver', cell: (d) => (<><div>{d.full_name ?? <span className="muted">no name yet</span>}</div><div className="mono muted">{d.phone ?? d.email ?? '—'}</div></>) },
              { head: 'Verification', shrink: true, cell: (d) => (
                <>
                  <State tone={kycTone(d.kyc_status)}>{humanise(d.kyc_status)}</State>
                  <small className="detail">step {d.onboarding_step} of 4</small>
                </>
              ) },
              { head: 'Docs', num: true, shrink: true, cell: (d) => <>{d.documents}{Number(d.in_review_count) ? <small className="detail">{d.in_review_count} to review</small> : null}</> },
              { head: 'Vehicle', cell: (d) => d.registration_no ? (<><div>{[d.make, d.model].filter(Boolean).join(' ')}</div><div className="mono muted">{d.registration_no}{d.plate_colour === 'white' ? <span className="flag" style={{ marginLeft: 6 }}>white plate</span> : null}</div></>) : <span className="muted">none yet</span> },
              { head: 'Routes', num: true, shrink: true, cell: (d) => d.active_routes },
              { head: 'Rides', num: true, shrink: true, cell: (d) => d.rides_completed },
              { head: 'Joined', shrink: true, cell: (d) => ago(d.created_at) },
            ]}
          />
        )}
      </Panel>
    </>
  );
}
